import _round from 'lodash/round';
import _get from 'lodash/get';

interface ControlChildrenInput {
    children: DirectedTreeNodeWithHref[];
    maxChildren: number;
    sortKey: 'plays' | 'earnings';
    trackIcon: string;
}

/**
 * If there are more children than maxChildren, sort the children by the sortKey and then summarize
 * all children over maxChildren into one child.
 * For example:

const children = [
    {
        entity_name: 'Coddled First Song',
        entity_type: 'track',
        plays: 2,
        earnings: 1.02,
    },
    {
        entity_name: 'Forgotten Middle Song',
        entity_type: 'track',
        plays: 1,
        earnings: 0.02,
    },
    {
        entity_name: 'Spoiled Third Song',
        entity_type: 'track',
        plays: 4,
        earnings: 3.02,
    },
];

const testResult = controlChildren({
    children,
    maxChildren: 1,
    sortKey: 'earnings'
});

console.log('testResult: ', testResult);

// prints:
// testResult:  [
//   {
//     entity_name: 'Spoiled Third Song',
//     entity_type: 'track',
//     plays: 4,
//     earnings: 3.02
//   },
//   {
//     earnings: 1.04,
//     plays: 3,
//     entity_name: '(2 tracks)',
//     entity_type: 'track'
//   }
// ]
*/
export const controlChildren = ({
    children,
    maxChildren,
    sortKey,
    trackIcon,
}: ControlChildrenInput): DirectedTreeNodeWithHref[] => {
    if (children.length <= maxChildren || maxChildren < 2) {
        return children;
    }

    const sortedChildren = [...children].sort((a, b): number => {
        let aSortValue = _get(a, sortKey, 0);
        let bSortValue = _get(b, sortKey, 0);

        if (aSortValue === null) {
            aSortValue = 0;
        }

        if (bSortValue === null) {
            bSortValue = 0;
        }

        if (aSortValue > bSortValue) {
            return -1;
        }

        if (aSortValue < bSortValue) {
            return 1;
        }

        return 0;
    });

    const unmodifiedChildren = sortedChildren.slice(0, maxChildren);
    const childrenToSummarize = sortedChildren.slice(
        maxChildren,
        children.length,
    );
    const originalEntityType = children[0].entity_type;

    const summarizedChild = childrenToSummarize.reduce(
        (acc, { earnings, plays }) => {
            if (
                earnings !== null &&
                earnings !== undefined &&
                acc.earnings !== null &&
                acc.earnings !== undefined
            ) {
                acc.earnings += earnings;
            }

            if (
                plays !== null &&
                plays !== undefined &&
                acc.plays !== null &&
                acc.plays !== undefined
            ) {
                acc.plays += plays;
            }

            return acc;
        },
        {
            earnings: 0,
            plays: 0,
            entity_name: null as Maybe<string>,
            entity_type: originalEntityType,
            children: null,
            href: trackIcon,
        },
    );

    if (
        summarizedChild.earnings !== null &&
        summarizedChild.earnings !== undefined
    ) {
        summarizedChild.earnings = _round(summarizedChild.earnings, 2);
    }

    const singularOrPluralEntityType =
        childrenToSummarize.length > 1
            ? `${originalEntityType}s`
            : originalEntityType;

    summarizedChild.entity_name = `(${childrenToSummarize.length} other ${singularOrPluralEntityType})`;

    return unmodifiedChildren.concat([summarizedChild]);
};
